import { useEffect, useState } from "react";

import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";

import { DashboardFilterType, ProductRatio } from "@/types/dashboard";

import {
  getDashboardBidFilterToString,
  getDashboardProjectFilterToString,
} from "@/consts/Dashboard";

import { formatKoreanCurrencyWithWon, formatToPercentString } from "@/libs/utils";

import { showToast } from "@/utils/toastUtils";

import DashboardCardHeader from "@/components/DashboardCardHeader";

const COLORS = ["#344BFD", "#F4A79D"];

const renderCustomLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
}: any) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  const displayPercent = formatToPercentString(percent);

  return (
    <g>
      <circle cx={x} cy={y} r={16} fill="#ECEAF8" strokeWidth={1} />
      <text
        x={x}
        y={y}
        fill="#000000"
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={12}
        fontWeight={700}
      >
        {displayPercent}
      </text>
    </g>
  );
};

type Props = {
  type: "bid" | "project";
  title: string;
  searchReady: boolean;
  method: any;
  filter: DashboardFilterType;
  onSearchComplete: () => void;
};

export default function DashboardPieChart({
  type = "bid",
  title,
  searchReady,
  method,
  filter,
  onSearchComplete,
}: Props) {
  const isProject = type === "project";
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [data, setData] = useState<ProductRatio[]>([]);
  const onLoad = async () => {
    try {
      setIsLoading(true);
      const params = isProject
        ? getDashboardProjectFilterToString(filter)
        : getDashboardBidFilterToString(filter);
      const result = await method(params);
      if (result.data.success) {
        const newData = result.data.pods[0].content.items.map((item: any) => ({
          label: isProject ? item.product_name : item.name,
          value: item.amount,
        }));
        setData(newData);
      } else {
        showToast({
          type: "error",
          message: `${title}을(를) 조회하는데 실패했습니다. ${result.data.error.type}`,
        });
      }
    } catch (e) {
      showToast({
        type: "error",
        message: `${title}을(를) 조회하는데 실패했습니다. ${e}`,
      });
    } finally {
      setIsLoading(false);
      onSearchComplete();
    }
  };

  useEffect(() => {
    if (searchReady) {
      onLoad();
    }
  }, [searchReady]);

  return (
    <DashboardCardHeader title={title} isLoading={isLoading}>
      <ResponsiveContainer width="100%" height={230}>
        <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={90}
            labelLine={false}
            label={renderCustomLabel}
            dataKey="value"
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={COLORS[index % COLORS.length]}
              />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>

      <div className="flex justify-center gap-6 text-sm mt-4">
        {data.map((entry, index) => (
          <div
            key={entry.label}
            className="flex items-center gap-1 text-sm font-semibold"
          >
            <span
              className="w-3 h-3 rounded-full inline-block"
              style={{ backgroundColor: COLORS[index % COLORS.length] }}
            />
            <span className="text-gray-A">{entry.label}</span>
            <span className="text-gray-B">
              {entry.value > 0 ? formatKoreanCurrencyWithWon(entry.value) : "0 원"}
            </span>
          </div>
        ))}
      </div>
    </DashboardCardHeader>
  );
}
